import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Form as FinalForm, Field } from 'react-final-form';
import { useConfiguration } from '../../../../context/configurationContext';
import { isOriginInUse } from '../../../../util/search';
import { LocationAutocompleteInput } from '../../../../components';
import css from './GymDirectorySection.module.css';
import Button from '../Button/Button';
import sectionBackground from '../../../../assets/how-it-works-section-2.png';

const identity = v => v;

// Check if Google Maps is fully loaded
const isGoogleMapsLoaded = () => {
  return typeof window !== 'undefined' && 
         window.google && 
         window.google.maps && 
         window.google.maps.places;
};

const GymDirectorySection = () => {
  const [selectedSport, setSelectedSport] = useState('');
  const [mapsReady, setMapsReady] = useState(false);
  const history = useHistory();
  const config = useConfiguration();

  useEffect(() => {
    // Check if maps are already loaded
    if (isGoogleMapsLoaded()) {
      setMapsReady(true);
      return;
    }

    // Poll for Google Maps to be loaded
    const checkInterval = setInterval(() => {
      if (isGoogleMapsLoaded()) {
        setMapsReady(true);
        clearInterval(checkInterval);
      }
    }, 100);

    // Cleanup after 10 seconds
    const timeout = setTimeout(() => {
      clearInterval(checkInterval);
      if (!isGoogleMapsLoaded()) {
        console.warn('Google Maps failed to load within timeout');
      }
    }, 10000);

    return () => {
      clearInterval(checkInterval);
      clearTimeout(timeout);
    };
  }, []);

  const handleFindGyms = (values) => {
    const params = new URLSearchParams();
    params.append('pub_listingType', 'gym');
    
    // Add sport filter if selected
    if (selectedSport && selectedSport !== '') {
      params.append('pub_gymType', `has_all:${selectedSport}`);
    }
    
    // Add location if entered
    if (values && values.location && values.location.selectedPlace) {
      const { search, selectedPlace } = values.location;
      const { origin, bounds } = selectedPlace;
      
      if (isOriginInUse(config) && origin) {
        params.append('origin', `${origin.lat},${origin.lng}`);
      }
      
      if (search) {
        params.append('address', search);
      }
      
      if (bounds) {
        params.append('bounds', `${bounds.ne.lat},${bounds.ne.lng},${bounds.sw.lat},${bounds.sw.lng}`);
      }
    }
    
    history.push(`/gym?${params.toString()}`);
  };

  return (
    <div className={css.section} style={{ backgroundImage: `url(${sectionBackground})` }}>
      <div className={css.header}>
        <p className={css.title}>
          Explore Martial Arts Gyms Across the UK
        </p>

        <p className={css.description}>
          Looking for a place to train? Use our gym directory to discover
          martial arts schools in your local area.
        </p>
      </div>

      <FinalForm
        onSubmit={handleFindGyms}
        render={({ handleSubmit, form }) => (
          <form 
            className={css.searchContainer}
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <select 
              className={css.selectInput}
              value={selectedSport}
              onChange={(e) => setSelectedSport(e.target.value)}
            >
              <option value="">Select your sport</option>
              <option value="boxing">Boxing</option>
              <option value="muay-thai">Muay Thai</option>
              <option value="jiu-jitsu">Brazilian Jiu-jitsu (BJJ)</option>
              <option value="judo">Judo</option>
              <option value="karate">Karate</option>
              <option value="aikido">Aikido</option>
              <option value="kickboxing">Kickboxing</option>
              <option value="sambo">Sambo</option>
              <option value="wrestling">Wrestling</option>
              <option value="taekwondo">Taekwondo</option>
              <option value="fencing">Fencing</option>
              <option value="mma">MMA (Mixed Martial Arts)</option>
            </select>

            <div className={css.locationInputWrapper}>
              {mapsReady ? (
                <Field
                  name="location"
                  format={identity}
                  render={({ input, meta }) => {
                  const { onChange, ...restInput } = input;
                  
                  const searchOnChange = value => {
                    onChange(value);
                    // Don't auto-submit - wait for user to click "Find gyms" button
                  };
                  
                  // Ensure input has required properties
                  const searchInput = {
                    ...restInput,
                    onChange: searchOnChange,
                    onFocus: restInput.onFocus || (() => {}),
                  };
                    
                    return (
                      <LocationAutocompleteInput
                        placeholder="Enter your location..."
                        className={css.locationAutocompleteRoot}
                        inputClassName={css.locationInput}
                        predictionsClassName={css.locationPredictions}
                        predictionsAttributionClassName={css.predictionsAttribution}
                        input={searchInput}
                        meta={meta || {}}
                      />
                    );
                  }}
                />
              ) : (
                <input
                  type="text"
                  className={css.locationInput}
                  placeholder="Loading location search..."
                  disabled
                />
              )}
            </div>

            <Button
              className={css.findButton}
              variant="primary"
              text="Find gyms"
              type="submit"
            />
          </form>
        )}
      />
    </div>
  );
};

export default GymDirectorySection;

